import React, { useContext, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SearchIcon from '@mui/icons-material/Search'
import API from '../../../../services/API'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import { EPCRContext } from '../EPCR'
import {
  mapParticipantToDemographics,
  participantDialogRows,
} from './participantMapping'

// Bib-number search against the linked event's uploaded participant list.
// Follows the CAD lookup flow in Incident/Details.jsx: debounced lookup, a
// confirm dialog first (participants sometimes swap bib numbers), and only
// then a multi-field write into demographics. Renders nothing unless the
// linked event has a participant list.
const ParticipantLookup = () => {
  const { formValues, setFormValues, setUploadState, setIsDraftDirty } =
    useContext(EPCRContext)
  const eventID =
    formValues?.incident?.linkedEvent?._id || formValues?.incident?.linkedEvent
  const [listMeta, setListMeta] = useState(null)
  const [number, setNumber] = useState('')
  const [lookupState, setLookupState] = useState('idle')
  const [pendingParticipant, setPendingParticipant] = useState(null)
  const [appliedNumber, setAppliedNumber] = useState(null)
  // A bib the user has explicitly declined in the confirm popup — don't
  // re-open the popup for it until the number changes.
  const declinedNumber = useRef(null)

  useEffect(() => {
    setListMeta(null)
    if (!eventID) return
    let cancelled = false
    API.get(`/epcr/event/${eventID}/participant-list-meta`).then((res) => {
      if (cancelled) return
      if (res.status === 200 && res.data?.hasList) setListMeta(res.data)
    })
    return () => {
      cancelled = true
    }
  }, [eventID])

  useEffect(() => {
    const trimmed = number.trim()
    if (!trimmed || !eventID) {
      setLookupState('idle')
      setPendingParticipant(null)
      declinedNumber.current = null
      return undefined
    }
    if (declinedNumber.current === trimmed) {
      setLookupState('idle')
      return undefined
    }

    setLookupState('loading')
    const timeout = setTimeout(() => {
      // API.js never rejects on HTTP errors, so check res.status explicitly
      API.get(
        `/epcr/event/${eventID}/participant?number=${encodeURIComponent(trimmed)}`,
      )
        .then((res) => {
          if (res.status === 200 && res.data?.participant) {
            setPendingParticipant({ ...res.data, number: trimmed })
            setLookupState('found')
            return
          }
          setPendingParticipant(null)
          setLookupState(res.status === 404 ? 'notFound' : 'error')
        })
        .catch(() => {
          setPendingParticipant(null)
          setLookupState('error')
        })
    }, 500)

    return () => clearTimeout(timeout)
  }, [number, eventID])

  if (!eventID || !listMeta) return null

  const participant = pendingParticipant?.participant

  const confirmParticipant = () => {
    if (!participant) return
    declinedNumber.current = null
    setFormValues((prev) => ({
      ...prev,
      demographics: {
        ...(prev.demographics || {}),
        ...mapParticipantToDemographics(participant, prev.demographics || {}),
      },
    }))
    setUploadState('pending')
    setIsDraftDirty(true)
    setAppliedNumber({
      number: participant.number,
      name: [participant.firstName, participant.lastName]
        .filter(Boolean)
        .join(' '),
    })
    setPendingParticipant(null)
    setLookupState('idle')
    setNumber('')
  }

  const declineParticipant = () => {
    if (pendingParticipant) declinedNumber.current = pendingParticipant.number
    setPendingParticipant(null)
    setLookupState('idle')
  }

  const adornment =
    lookupState === 'loading' ? (
      <CircularProgress size={18} />
    ) : appliedNumber ? (
      <CheckCircleIcon color="success" fontSize="small" />
    ) : (
      <SearchIcon color="disabled" fontSize="small" />
    )

  return (
    <EPCRCard>
      <Typography variant="title2">Participant Lookup</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={4}>
          <TextField
            fullWidth
            label="Bib / Race Number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            helperText={
              lookupState === 'notFound'
                ? `No participant with bib ${number.trim()}`
                : lookupState === 'error'
                  ? 'The participant list could not be searched right now.'
                  : `Search the event's entry list (${listMeta.count} participants)`
            }
            error={lookupState === 'error'}
            InputProps={{
              endAdornment: <InputAdornment position="end">{adornment}</InputAdornment>,
            }}
          />
        </Grid>
        {appliedNumber && (
          <Grid item xs={12} lg={8}>
            <Alert severity="success">
              Applied details for bib {appliedNumber.number}
              {appliedNumber.name ? ` — ${appliedNumber.name}` : ''}. Check
              them before continuing.
            </Alert>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={!!pendingParticipant}
        onClose={declineParticipant}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Participant Found</DialogTitle>
        <DialogContent>
          <Stack gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              Participants sometimes swap bib numbers. Confirm these details
              match the patient in front of you before applying.
            </Typography>
            {pendingParticipant?.multipleMatches && (
              <Alert severity="warning">
                More than one participant shares this number — showing the
                first.
              </Alert>
            )}
            <Stack gap={0.75}>
              {participant &&
                participantDialogRows(participant, (dob) =>
                  `${moment(dob).format('DD/MM/YYYY')} (${moment().diff(dob, 'years')}y)`,
                ).map(([label, value]) => (
                  <Stack key={label} direction="row" gap={1}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 120, flexShrink: 0 }}
                    >
                      {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {value}
                    </Typography>
                  </Stack>
                ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={declineParticipant} color="inherit">
            Not This Person
          </Button>
          <Button onClick={confirmParticipant} variant="contained">
            Use These Details
          </Button>
        </DialogActions>
      </Dialog>
    </EPCRCard>
  )
}

export default ParticipantLookup
